/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";

import { CircularProgress, Grid, Stack, TextField, Tooltip } from "@mui/material";

import { createStyles, makeStyles } from "@mui/styles";

import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import Checkbox from "@mui/material/Checkbox";

import Card from "@mui/material/Card";

import Typography from "@mui/material/Typography";

import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LoopIcon from "@mui/icons-material/Loop";

import Divider from "@mui/material/Divider";

import Box from "@mui/material/Box";

import {
    ChecklistItem,
    Effect,
    ExpandedWorkOrder,
    FullAssignedTechnicianEntry,
    Nullable,
    Setter,
    TechnicianEntry,
} from "../../../utils/Interfaces";

import { selectRoles } from "../../auth/authSlice";
import { deleteTechnicianEntry, modifyTechnicianEntry } from "./technicianEntryFunctions";
import ConfirmDeleteModal from "../../../components/confirmation/ConfirmDeleteModal";
import { handleS3PDFDownload, S3PDFRoute, sanitizeFileName } from "../../../utils/Functions";
import MultilineEditableField from "../../../components/form-controls/MultilineEditableField";
import TimeRow from "../../../components/form-controls/TimeRow";
import TechnicianChecklistItem from "../../../components/form-controls/TechnicianChecklistItem";
import TechnicianEntryAssignments from "./TechnicianEntryAssignments";
import AmendableEditableParagraph from "../../../components/form-controls/AmendableEditableParagraph";
import AttachmentManager from "../../../components/general/AttachmentManager";
import { MaterialsTable } from "../../../components/table/MaterialsTable";
import { Add } from "@mui/icons-material";
import { addTasksToTechnicianEntry } from "../workOrderFunctions";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { ModelName } from "../../../utils/Requests";

const useStyles = makeStyles(() =>
    createStyles({
        rotateIcon: {
            animation: "$spin 1s linear infinite",
        },
        "@keyframes spin": {
            "0%": {
                transform: "rotate(360deg)",
            },
            "100%": {
                transform: "rotate(0deg)",
            },
        },
    })
);

export interface OfficeTechnicianEntryProps {
    workOrder: ExpandedWorkOrder;
    techEntry: FullAssignedTechnicianEntry;
    tripNumber: number;
    onTechnicianEntryModified: Setter<Effect>;
}

const OfficeTechnicianEntry = (props: OfficeTechnicianEntryProps) => {
    const { workOrder, techEntry, tripNumber, onTechnicianEntryModified } = props;

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const readOnly: boolean = !useAppSelector(selectRoles).includes("office");

    const classes = useStyles();

    const [processingPDFRequest, setProcessingPDFRequest] = useState<boolean>(false);

    const [expanded, setExpanded] = useState<boolean>(false);

    const [travelStartTime, setTravelStartTime] = useState<Nullable<string>>(techEntry.travel_start_time);
    const [timeArrivedOnSite, setTimeArrivedOnSite] = useState<Nullable<string>>(techEntry.time_arrived_on_site);
    const [timeSiteLeft, setTimeSiteLeft] = useState<Nullable<string>>(techEntry.time_site_left);

    const [returnTripNeeded, setReturnTripNeeded] = useState<boolean>(techEntry.return_trip_needed);

    const [workPerformed, setWorkPerformed] = useState<Nullable<string>>(techEntry.work_performed);
    const [workPerformedAmended, setWorkPerformedAmended] = useState<Nullable<string>>(techEntry.work_performed_amended);

    const [taskToAdd, setTaskToAdd] = useState<string>("");
    const [isAddingToChecklist, setIsAddingToChecklist] = useState<boolean>(false);

    const [partsNeededToReturn, setPartsNeededToReturn] = useState<Nullable<string>>(techEntry.parts_needed_to_return);

    const [partsNeededToReturnAmended, setPartsNeededToReturnAmended] = useState<Nullable<string>>(
        techEntry.parts_needed_to_return_amended
    );

    const [otherNotes, setOtherNotes] = useState<Nullable<string>>(techEntry.other_notes);
    const [otherNotesAmended, setOtherNotesAmended] = useState<Nullable<string>>(techEntry.other_notes_amended);

    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);

    let darkTheme = localStorage.getItem("techwiseTheme") === "dark";

    const handleClose = () => {
        setIsConfirmModalOpen(false);
    };

    const readOnlyWorkOrderStatuses = ["CM", "SB"];

    const deleteTechEntryModal: JSX.Element = (
        <ConfirmDeleteModal
            onDelete={(modalCallback: Effect) => {
                deleteTechnicianEntry(techEntry.id, dispatch, () => {
                    onTechnicianEntryModified(() => {});
                    modalCallback();
                });
            }}
            confirmText={`delete trip ${tripNumber}`}
            open={isConfirmModalOpen}
            handleClose={handleClose}
            description={
                "You are about to delete the following technician form, including all assignments, notes and attachments."
            }
        />
    );

    const submittedGreenText: JSX.Element = (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    marginRight: "10px",
                }}
            >
                <Typography variant="body2" color="green">
                    SUBMITTED
                </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
        </>
    );

    let printPDFButton: JSX.Element = (
        <Tooltip title="Generate Trip PDF">
            <IconButton
                onClick={() =>
                    handleS3PDFDownload(
                        S3PDFRoute.TechnicianEntry,
                        techEntry.id,
                        dispatch,
                        setProcessingPDFRequest,
                        sanitizeFileName(`${workOrder.ticket_no}-${tripNumber}.pdf`)
                    )
                }
                size="large"
                disabled={readOnly}
            >
                <PictureAsPdfIcon />
            </IconButton>
        </Tooltip>
    );

    if (processingPDFRequest) {
        printPDFButton = (
            <Tooltip title={"Processing Print Request..."}>
                <IconButton disabled size="large">
                    <LoopIcon className={classes.rotateIcon} />
                </IconButton>
            </Tooltip>
        );
    }

    if (!expanded) {
        return (
            <Card sx={{ minWidth: 275 }} style={{ marginBottom: "25px", padding: "25px" }}>
                {deleteTechEntryModal}

                <Grid container direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontSize: 24, mb: 1.5, mt: 2.5 }} color="text.primary">
                        {`Trip #${techEntry.trip_number}`}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                        }}
                    >
                        {techEntry.submitted && submittedGreenText}
                        {printPDFButton}
                        <Tooltip title="Delete Technician Form">
                            <IconButton onClick={() => setIsConfirmModalOpen(true)} size="large" disabled={readOnly}>
                                <DeleteIcon color={readOnly ? "secondary" : "error"} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Expand Technician Form">
                            <IconButton onClick={() => setExpanded(true)} size="large">
                                <ExpandMoreIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Grid>
            </Card>
        );
    }

    return (
        <Card sx={{ minWidth: 275 }} style={{ marginBottom: "25px", padding: "25px" }}>
            {deleteTechEntryModal}

            <Grid container direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: 24, mb: 1.5, mt: 2.5 }} color="text.primary">
                    {`Trip #${techEntry.trip_number}`}
                </Typography>
                {(workOrder.status === "SB" || workOrder.status === "CM") && (
                    <Box>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                            }}
                        >
                            {techEntry.submitted && submittedGreenText}
                            {printPDFButton}
                            <Tooltip title={"Delete Technician Form"}>
                                <IconButton onClick={() => setIsConfirmModalOpen(true)} size="large" disabled={readOnly}>
                                    <DeleteIcon color={readOnly ? "secondary" : "error"} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={"Minimize Technician Form"}>
                                <IconButton onClick={() => setExpanded(false)} size="large">
                                    <ExpandLessIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                )}
                {!(workOrder.status === "SB" || workOrder.status === "CM") && (
                    <Box>
                        {printPDFButton}
                        <Tooltip title={"Delete Technician Form"}>
                            <IconButton onClick={() => setIsConfirmModalOpen(true)} size="large" disabled={readOnly}>
                                <DeleteIcon color={readOnly ? "secondary" : "error"} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Minimize Technician Form">
                            <IconButton onClick={() => setExpanded(false)} size="large">
                                <ExpandLessIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Grid>

            <Box sx={{ marginBottom: "20px" }} />

            <MultilineEditableField<TechnicianEntry>
                body={techEntry.comments ?? ""}
                title="Office Notes"
                updateKey="comments"
                updatePath={`/api/tech-entries/update/${techEntry.id}`}
                onUpdated={() => onTechnicianEntryModified(() => {})}
                lines={4}
                editable={!readOnly}
            />

            <Box sx={{ marginBottom: "20px" }} />

            <MultilineEditableField
                body={techEntry.signature ?? ""}
                title="Manager/AC Signature"
                updateKey="signature"
                updatePath={`/api/tech-entries/update/${techEntry.id}`}
                onUpdated={() => onTechnicianEntryModified(() => {})}
                lines={1}
                editable={false}
            />

            <Box sx={{ mb: 2 }} />

            <TechnicianEntryAssignments
                technicianEntry={techEntry}
                editing={!readOnlyWorkOrderStatuses.includes(workOrder.status) && !techEntry.submitted && !readOnly}
            />

            <Box>
                <Divider sx={{ mt: 2 }}>
                    <Typography variant="h5">Recorded Times &amp; Work Performed</Typography>
                </Divider>

                {/* Don't allow Office workers to modify the checklist. */}
                <TimeRow editing={false} field={travelStartTime} setField={setTravelStartTime} label="Travel Start Time" />
                <TimeRow editing={false} field={timeArrivedOnSite} setField={setTimeArrivedOnSite} label="Time Arrived on Site" />
                <TimeRow editing={false} field={timeSiteLeft} setField={setTimeSiteLeft} label="Time Left" />

                <AmendableEditableParagraph<TechnicianEntry>
                    field={workPerformedAmended}
                    setField={setWorkPerformedAmended}
                    parentField={workPerformed}
                    setParentField={setWorkPerformed}
                    updatePath={`/api/tech-entries/update/${techEntry.id}`}
                    label="Work Performed"
                    name="work_performed_amended"
                    parentName="work_performed"
                    originalDescription={workPerformed}
                    onUpdate={() => onTechnicianEntryModified(() => {})}
                    editable={!readOnly}
                />

                <Divider sx={{ mt: 2 }}>
                    <Typography variant="h5">Parts/Materials User</Typography>
                </Divider>

                <Box sx={{ mb: 2 }} />

                {techEntry.materials.length === 0 &&
                    (techEntry.materials_write_in === null || techEntry.materials_write_in === "") && (
                        <Typography textAlign="center">No parts/materials recorded.</Typography>
                    )}

                {techEntry.materials.length > 0 && (
                    <MaterialsTable
                        editing={false}
                        technicianEntry={techEntry}
                        addMaterial={() => {}}
                        updateMaterial={() => {}}
                        showMaterialPrice={true}
                    />
                )}

                {techEntry.materials_write_in !== null && techEntry.materials_write_in !== "" && (
                    <Box
                        sx={{
                            backgroundColor: darkTheme ? "#303030" : "#F2F2F2",
                            borderRadius: 2,
                            p: 2,
                            mb: 2,
                            mt: 2,
                        }}
                    >
                        <Typography fontWeight="bold">Manual Part/Material Entry:&nbsp;</Typography>
                        <Typography component="span">{techEntry.materials_write_in}</Typography>
                    </Box>
                )}

                <Divider sx={{ mt: 2 }}>
                    <Typography variant="h5">Technician Comments</Typography>
                </Divider>

                <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ fontSize: 18 }} color="text.primary" fontWeight="bold">
                        Return Trip Needed
                    </Typography>
                    <Checkbox
                        id="return_trip_needed"
                        name="return_trip_needed"
                        onClick={() => {
                            let newReturnTripNeeded = !returnTripNeeded;
                            setReturnTripNeeded(newReturnTripNeeded);
                            modifyTechnicianEntry(
                                techEntry.id,
                                "return_trip_needed",
                                newReturnTripNeeded,
                                dispatch,
                                () => onTechnicianEntryModified(() => {}),
                                () => {}
                            );
                        }}
                        checked={returnTripNeeded}
                        disabled={readOnly}
                    />
                </Stack>

                <Box sx={{ mb: 2 }} />

                <AmendableEditableParagraph<TechnicianEntry>
                    field={partsNeededToReturnAmended}
                    setField={setPartsNeededToReturnAmended}
                    parentField={partsNeededToReturn}
                    setParentField={setPartsNeededToReturn}
                    updatePath={`/api/tech-entries/update/${techEntry.id}`}
                    label="Parts Needed to Return"
                    name="parts_needed_to_return_amended"
                    parentName="parts_needed_to_return"
                    originalDescription={partsNeededToReturn}
                    onUpdate={() => onTechnicianEntryModified(() => {})}
                    editable={!readOnly}
                />

                <AmendableEditableParagraph<TechnicianEntry>
                    field={otherNotesAmended}
                    setField={setOtherNotesAmended}
                    parentField={otherNotes}
                    setParentField={setOtherNotes}
                    updatePath={`/api/tech-entries/update/${techEntry.id}`}
                    label="Other Notes"
                    name="other_notes_amended"
                    parentName="other_notes"
                    originalDescription={otherNotes}
                    onUpdate={() => onTechnicianEntryModified(() => {})}
                    editable={!readOnly}
                />

                <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
                    <Typography variant="h6">Service Channel Ticket #</Typography>
                    <Typography>
                        {techEntry.service_channel_ticket_no === null ||
                        techEntry.service_channel_ticket_no === undefined ||
                        techEntry.service_channel_ticket_no === ""
                            ? "--"
                            : techEntry.service_channel_ticket_no}
                    </Typography>
                </Stack>
                <Box sx={{ marginBottom: "10px" }} />

                <Divider sx={{ mt: 2 }}>
                    <Typography variant="h5">Technician Checklist</Typography>
                </Divider>

                <Box sx={{ mb: 2 }} />

                {/* Don't allow Office workers to modify the checklist. */}
                {techEntry.tasks.map((checklistItem: ChecklistItem, index: number) => {
                    return (
                        <TechnicianChecklistItem
                            key={index}
                            editable={!techEntry.submitted && !readOnly}
                            onChange={onTechnicianEntryModified}
                            checklistItem={checklistItem}
                            contrastBg={index % 2 === 1}
                        />
                    );
                })}

                {!techEntry.submitted && (
                    <Stack direction="row" justifyContent="flex-start">
                        <TextField
                            disabled={readOnly || isAddingToChecklist}
                            size="small"
                            label="Add task to checklist"
                            value={taskToAdd}
                            onChange={(event) => setTaskToAdd(event.target.value)}
                            sx={{ mt: 2, width: 600 }}
                            InputProps={{
                                endAdornment: (
                                    <Tooltip title="Add checklist task">
                                        <span>
                                            <IconButton
                                                disabled={!taskToAdd}
                                                sx={{ mr: -1 }}
                                                onClick={() => {
                                                    setIsAddingToChecklist(true);
                                                    addTasksToTechnicianEntry(techEntry.id, [taskToAdd], dispatch, () =>
                                                        onTechnicianEntryModified(() => {
                                                            setIsAddingToChecklist(false);
                                                        })
                                                    );
                                                    setTaskToAdd("");
                                                }}
                                            >
                                                <Add fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                ),
                            }}
                        />

                        {isAddingToChecklist && (
                            <Stack direction="column" justifyContent="center" sx={{ ml: 2 }}>
                                <CircularProgress size={20} sx={{ ml: 1, mt: 2 }} />
                            </Stack>
                        )}
                    </Stack>
                )}
            </Box>

            <Divider sx={{ mt: 2, mb: 2 }}>
                <Typography variant="h5">Attached Files</Typography>
            </Divider>

            <AttachmentManager readOnly={readOnly} model={ModelName.TechnicianEntry} idKey={techEntry.id} editing={!readOnly} />
        </Card>
    );
};

export default OfficeTechnicianEntry;
